import type { WaitPath } from "../types";
import type { GapSpec } from "./gap_art";
import type { ToothSpec } from "./tooth_art";

/** One drawing of a strip: the side view of one tooth, or the gap scene for a missing tooth. */
export type WaitStage = { art: "tooth"; spec: ToothSpec } | { art: "gap"; spec: GapSpec };
type Stages = readonly [WaitStage, WaitStage, WaitStage, WaitStage];

/** What can happen to the tooth if treatment waits: one drawing per step, now to the worst case. */
export const WAIT_PATH_STAGES: Record<WaitPath, Stages> = {
  // A small crack, a crack into the root, a broken cusp with an inflamed nerve, the tooth lost.
  crack: [
    { art: "tooth", spec: { overlays: ["crack_small"], pulp: "ok" } },
    { art: "tooth", spec: { overlays: ["crack_long"], pulp: "ok" } },
    { art: "tooth", spec: { overlays: ["crack_long", "broken_cusp"], pulp: "hot" } },
    { art: "tooth", spec: { dashed: true } },
  ],
  // Decay in the outer layer, deeper, at the nerve, then an infection at the root tip.
  decay_to_nerve: [
    { art: "tooth", spec: { overlays: ["decay_small"], pulp: "ok" } },
    { art: "tooth", spec: { overlays: ["decay_deep"], pulp: "ok" } },
    { art: "tooth", spec: { overlays: ["decay_nerve"], pulp: "hot" } },
    { art: "tooth", spec: { overlays: ["decay_nerve", "abscess"], pulp: "dead" } },
  ],
  // An inflamed nerve, the nerve dies, an abscess at the root tip, the tooth lost.
  nerve_infection: [
    { art: "tooth", spec: { overlays: ["decay_nerve"], pulp: "hot" } },
    { art: "tooth", spec: { overlays: ["decay_nerve"], pulp: "dead" } },
    { art: "tooth", spec: { overlays: ["decay_nerve", "abscess"], pulp: "dead" } },
    { art: "tooth", spec: { dashed: true } },
  ],
  // Tartar and a shallow pocket, a deeper pocket with inflamed gums, bone loss, a loose tooth.
  gum_disease: [
    {
      art: "tooth",
      spec: { overlays: ["tartar"], gum: 86, bone: 104, pulp: "ok", pocket: true },
    },
    {
      art: "tooth",
      spec: {
        overlays: ["tartar_heavy"],
        gum: 92,
        bone: 120,
        pulp: "ok",
        pocket: true,
        hotGums: true,
      },
    },
    {
      art: "tooth",
      spec: {
        overlays: ["tartar_heavy"],
        gum: 100,
        bone: 136,
        pulp: "ok",
        pocket: true,
        hotGums: true,
      },
    },
    {
      art: "tooth",
      spec: {
        overlays: ["tartar_heavy"],
        gum: 104,
        bone: 146,
        pulp: "ok",
        pocket: true,
        hotGums: true,
        tilt: 11,
      },
    },
  ],
  // An old filling, a crack beside it, a cusp breaks, half the tooth breaks.
  worn_fillings: [
    { art: "tooth", spec: { overlays: ["amalgam"], pulp: "ok" } },
    { art: "tooth", spec: { overlays: ["amalgam_crack"], pulp: "ok" } },
    { art: "tooth", spec: { overlays: ["amalgam", "broken_cusp"], pulp: "ok" } },
    { art: "tooth", spec: { overlays: ["big_break"], pulp: "hot" } },
  ],
  // Everything upright, the neighbors tip toward the gap, the tooth across drifts into the space, the bone sinks.
  missing_tooth: [
    { art: "gap", spec: { lean: 0, drift: 0, boneDrop: 0 } },
    { art: "gap", spec: { lean: 9, drift: 0, boneDrop: 0 } },
    { art: "gap", spec: { lean: 9, drift: 18, boneDrop: 0 } },
    { art: "gap", spec: { lean: 9, drift: 18, boneDrop: 20 } },
  ],
  // A root-filled tooth, a crack, a broken cusp, the tooth lost (SPEC2's drawings; the dentist reviews stage 2's
  // pairing with its caption). No shadow: no strip stage has one.
  after_root_canal: [
    { art: "tooth", spec: { overlays: ["canals_filled"] } },
    { art: "tooth", spec: { overlays: ["canals_filled", "crack_long"] } },
    { art: "tooth", spec: { overlays: ["canals_filled", "crack_long", "broken_cusp"] } },
    { art: "tooth", spec: { dashed: true } },
  ],
};

// The healthy gum line and bone crest, drawn so an infection can be seen leaving the tooth for the bone.
const TISSUE = { gum: 86, bone: 104 } as const;

/**
 * A path as one family's work tells it, by the deck's variant. An extraction's never ends with the tooth lost, which
 * is the plan: it shows what the problem can do around the tooth while it stays in.
 */
export const WAIT_PATH_VARIANT_STAGES: Partial<Record<WaitPath, Partial<Record<string, Stages>>>> =
  {
    // A crack too deep to repair, bacteria getting in, an infection at the root, the infection in the bone.
    crack: {
      extraction: [
        { art: "tooth", spec: { ...TISSUE, overlays: ["crack_long"], pulp: "ok" } },
        { art: "tooth", spec: { ...TISSUE, overlays: ["crack_long"], pulp: "hot" } },
        { art: "tooth", spec: { ...TISSUE, overlays: ["crack_long", "abscess"], pulp: "dead" } },
        {
          art: "tooth",
          spec: { ...TISSUE, overlays: ["crack_long", "abscess_spread"], pulp: "dead" },
        },
      ],
    },
    // An infected nerve, an abscess at the root, the infection in the bone, then swollen gums.
    nerve_infection: {
      extraction: [
        { art: "tooth", spec: { ...TISSUE, overlays: ["decay_nerve"], pulp: "hot" } },
        { art: "tooth", spec: { ...TISSUE, overlays: ["decay_nerve", "abscess"], pulp: "dead" } },
        {
          art: "tooth",
          spec: { ...TISSUE, overlays: ["decay_nerve", "abscess_spread"], pulp: "dead" },
        },
        {
          art: "tooth",
          spec: {
            ...TISSUE,
            overlays: ["decay_nerve", "abscess_spread"],
            pulp: "dead",
            hotGums: true,
          },
        },
      ],
    },
  };
